import React, { useState, useEffect } from "react";
import {
  useGetPaginatedProductsQuery,
  usePrefetch,
} from "../features/products/productsApiSlice";
import { Product } from "../components/Product";
import { useDispatch, useSelector } from "react-redux";
import PaginationProductBox from "../components/ui/PaginationProductBox";
import Loading from "../components/Loading";
import useDummyElementWidth from "../hooks/useDummyElementWidth";
import { changePageNumber } from "../features/products/productSlice";
import { changeMainView } from "../features/general/generalSlice";
import useAuth from "../hooks/useAuth";
import FilterBar from "../components/FilterBar";
import { useGetCategoriesQuery } from "../features/categories/categoriesApiSlice";
import useCategory from "../hooks/useCategory";
import { Slide } from "react-slideshow-image";
import "react-slideshow-image/dist/styles.css";
import left_arrow from "../assets/images/left_arrow.svg";
import right_arrow from "../assets/images/right_arrow.svg";
import sell_buy from "../assets/images/sell-buy.png";

const Products = () => {
  const { componentHeight } = useSelector((state) => state?.general);
  const { id } = useAuth();
  const dispatch = useDispatch();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAll, setShowAll] = useState("true");
  const [need, setNeed] = useState(() => {
    // Check if there's a saved value in localStorage
    const savedNeed = localStorage.getItem("need");
    // If there's a saved value, use it; otherwise, default to "Sell"
    return savedNeed ? savedNeed : "Sell";
  });

  // Carousel data - you can replace these with your actual images later
  const carouselData = [
    {
      image: sell_buy,
      title: "",
      subtitle: ""
    },
    {
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&h=300&fit=crop",
      title: "Quality Guaranteed",
      subtitle: "Shop with confidence"
    },
    {
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=1200&h=300&fit=crop",
      title: "Best Deals Today",
      subtitle: "Don't miss out on great offers"
    }
  ];

  // Carousel properties
  const carouselProperties = {
    duration: 4000,
    transitionDuration: 150,
    arrows: true,
    autoplay: true,
    pauseOnHover: false,
    indicators: false,
    prevArrow: (
      <button className="absolute left-2 top-[25%] -translate-y-1/2 rounded-full z-10 text-white transition-all">
        <img src={left_arrow} alt="Previous" className="w-8 h-8" />
      </button>
    ),
    nextArrow: (
      <button className="absolute right-2 top-[25%] -translate-y-1/2 rounded-full z-10 text-white transition-all">
        <img src={right_arrow} alt="Next" className="w-8 h-8" />
      </button>
    ),
  };

  if (need) {
    dispatch(changeMainView(need));
  }

  const changeNeed = () => {
    if (need === "Sell") {
      setNeed("Request");
      dispatch(changeMainView("Request"));
    } else {
      setNeed("Sell");
      dispatch(changeMainView("Sell"));
    }
  };

  let initiatePage = localStorage.getItem("sellPageNo");
  if (!initiatePage) {
    localStorage.setItem("sellPageNo", 1);
  }
  initiatePage = localStorage.getItem("requestPageNo");
  if (!initiatePage) {
    localStorage.setItem("requestPageNo", 1);
  }

  let pageNumber =
    need === "Sell"
      ? localStorage.getItem("sellPageNo")
      : localStorage.getItem("requestPageNo");

  const { category, isLoading: categoryLoading, isSuccess: categorySuccess } = useCategory();
  
  useEffect(() => {
    localStorage.setItem("need", need);
    if (need === "Sell") {
      pageNumber = localStorage.getItem("sellPageNo");
    } else {
      pageNumber = localStorage.getItem("requestPageNo");
    }
  }, [need]);

  if (pageNumber) {
    dispatch(changePageNumber(pageNumber));
  }

  const handleCategorySelect = ({ category, all }) => {
    console.log('Category selected in Products:', { category, all });
    setSelectedCategory(category);
    setShowAll(all);
    // Reset to page 1 when changing category
    dispatch(changePageNumber(1));
    if (need === "Sell") {
      localStorage.setItem("sellPageNo", 1);
    } else {
      localStorage.setItem("requestPageNo", 1);
    }
  };

  const queryParams = {
    page: Number(useSelector((state) => state?.products.pageNumber)),
    need: useSelector((state) => state?.general.mainView),
    userId: id,
    categoryId: selectedCategory,  // Changed from category to categoryId
    all: showAll
  };

  console.log('Query params being sent:', queryParams);

  const {
    data: paginatedProducts,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetPaginatedProductsQuery(queryParams, {
    // Force refetch when category changes
    refetchOnMountOrArgChange: true
  });

  const prefetchPage = usePrefetch("getPaginatedProducts");
  useEffect(() => {
    if (pageNumber && pageNumber > 1) {
      prefetchPage({ 
        page: Number(pageNumber) - 1, 
        need, 
        userId: id, 
        categoryId: selectedCategory,  // Changed from category to categoryId
        all: showAll 
      });
    }
    if (pageNumber && pageNumber < paginatedProducts?.numberOfPages) {
      prefetchPage({ 
        page: Number(pageNumber) + 1, 
        need, 
        userId: id, 
        categoryId: selectedCategory,  // Changed from category to categoryId
        all: showAll 
      });
    }
    if (need === "Sell") {
      prefetchPage({
        page: Number(localStorage.getItem("requestPageNo")),
        need: "Request",
        userId: id,
        categoryId: selectedCategory,  // Changed from category to categoryId
        all: showAll
      });
    } else {
      prefetchPage({
        page: Number(localStorage.getItem("sellPageNo")),
        need: "Sell",
        userId: id,
        categoryId: selectedCategory,  // Changed from category to categoryId
        all: showAll
      });
    }
  }, [
    pageNumber,
    isSuccess,
    need,
    paginatedProducts?.numberOfPages,
    prefetchPage,
    selectedCategory,
    showAll
  ]);

  const noOfProducts = paginatedProducts?.data?.length;

  const result = useDummyElementWidth(noOfProducts);

  let content;

  if (isLoading) {
    content = <Loading />;
  }

  if (!isLoading && isError) {
    content = <p>{error?.data?.message}</p>;
  }

  if (!isError && !isLoading && paginatedProducts?.data?.length === 0) {
    content = <p>No Product Found!</p>;
  }
  if (isSuccess) {
    const tableContent = paginatedProducts?.data?.length
      ? paginatedProducts.data.map((product) =>
          need !== "All" ? (
            product.need === need && (
              <Product key={product?._id} product={product} />
            )
          ) : (
            <Product key={product?._id} product={product} />
          )
        )
      : null;

    content = (
      <div
        className="relative bg-custom-content-bg w-full pt-[37px] flex flex-col justify-start items-center min-w-[220px]"
        style={{ minHeight: `${componentHeight}vh` }}
      >
        <FilterBar 
          need={need} 
          changeNeed={changeNeed} 
          isSuccess={categorySuccess} 
          data={category} 
          onCategorySelect={handleCategorySelect}
        />
        
        {/* Carousel Section */}
        <div className="w-full">
          <div className="w-[100%] mx-auto">
            <Slide {...carouselProperties}>
              {carouselData.map((slide, index) => (
                <div key={index} className="relative w-full h-[500px]">
                  <img
                    src={slide.image}
                    alt={`Slide ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 via-black/60 to-white/100" />
                  <div className="absolute inset-0 flex flex-col justify-start pt-20 items-start px-12 text-white">
                    <h2 className="text-4xl font-bold mb-2">{slide.title}</h2>
                    <p className="text-xl opacity-90">{slide.subtitle}</p>
                  </div>
                </div>
              ))}
            </Slide>
          </div>
        </div>

        <div className="w-[95%] -mt-60 z-30">
          <div className="grid grid-cols-1 xxs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 w-full px-5 mb-5">
            {tableContent}
          </div>
        </div>

        <PaginationProductBox
          currentPage={paginatedProducts?.currentPage}
          numberOfPages={paginatedProducts?.numberOfPages}
          need={need}
        />
      </div>
    );
  }

  return content;
};

export default Products;
